import { useState, useEffect } from 'react';
import { UserPlus, Copy, CheckCheck, MoreHorizontal, ShieldOff } from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { RoleAssignmentPanel, SsoUserBadge } from '@grc/shared-ui';
import type { RoleDescriptor } from '@grc/shared-ui';
import {
  useTeamMembers,
  useInviteTeamMember,
  useUpdateTeamMember,
  useUpdateMemberRoles,
  useResetTeamMemberPassword,
  useResetTeamMemberMfa,
  useResendTeamMemberInvitation,
  useDeleteTeamMember,
  useUserAdminActor,
} from '@/api/hooks/useSettings';
import type { UserAdminActor } from '@/api/hooks/useSettings';
import { useAuthStore } from '@/store/auth.store';
import { PrimaryAdminReferenceCard } from '@/components/settings/PrimaryAdminReferenceCard';
import type { TeamMember } from '@/api/types/settings.types';

const MEMBER_ROLE_ID = '00000001-0000-0000-0000-000000000009';
const LOCKED_LABEL = 'Managed by admins';

// The role options come from the server, per actor (GET /users/assignable-roles): a user_admin sees super_admin and
// user_admin locked, a super_admin sees every role assignable. Nothing here decides access — the server does.
interface RoleOptions {
  roles: RoleDescriptor[];
  lockedIds: string[];
  nameToId: Record<string, string>;
}

function roleOptions(actor: UserAdminActor | undefined): RoleOptions {
  const all = actor?.roles ?? [];
  return {
    roles: all.map((r) => ({ id: r.roleId, name: r.name, description: r.description })),
    lockedIds: all.filter((r) => !r.assignable).map((r) => r.roleId),
    nameToId: Object.fromEntries(all.map((r) => [r.name, r.roleId])),
  };
}

// ── Invite modal ──────────────────────────────────────────────────────────────

function InviteModal({ options, onClose }: { options: RoleOptions; onClose: () => void }) {
  const inviteMutation = useInviteTeamMember();
  const [form, setForm] = useState({ email: '', firstName: '', lastName: '', selectedRoleIds: [MEMBER_ROLE_ID] });
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    inviteMutation.mutate(
      { email: form.email, firstName: form.firstName, lastName: form.lastName, role_ids: form.selectedRoleIds },
      {
        onSuccess: (r) => setInviteUrl(r.invitationUrl),
        onError: () => toast.error('Failed to send invitation'),
      },
    );
  }

  function handleCopy() {
    if (!inviteUrl) return;
    navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Invite Team Member</DialogTitle></DialogHeader>
        {inviteUrl ? (
          <div className="space-y-4">
            <p className="text-sm text-text-secondary">Invitation created. Share this link:</p>
            <div className="rounded-lg border border-border-subtle bg-bg-surface-2 p-3 flex items-center gap-2">
              <p className="text-xs font-mono text-text-primary flex-1 break-all line-clamp-2">{inviteUrl}</p>
              <button onClick={handleCopy} className="text-text-secondary hover:text-text-primary shrink-0">
                {copied ? <CheckCheck className="h-4 w-4 text-accent-emerald" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>
            <Button className="w-full" onClick={onClose}>Done</Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Email <span className="text-accent-rose">*</span></Label>
              <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>First Name</Label>
                <Input value={form.firstName} onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label>Last Name</Label>
                <Input value={form.lastName} onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Roles <span className="text-accent-rose">*</span></Label>
              <div className="space-y-2 rounded-md border border-border-subtle bg-bg-surface-2 p-3">
                {options.roles.map((r) => {
                  const locked = options.lockedIds.includes(r.id);
                  return (
                    <label
                      key={r.id}
                      className={`flex items-start gap-2.5 select-none ${locked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                      title={locked ? LOCKED_LABEL : undefined}
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5 shrink-0"
                        checked={form.selectedRoleIds.includes(r.id)}
                        disabled={locked}
                        onChange={() => setForm((f) => ({
                          ...f,
                          selectedRoleIds: f.selectedRoleIds.includes(r.id)
                            ? f.selectedRoleIds.filter((x) => x !== r.id)
                            : [...f.selectedRoleIds, r.id],
                        }))}
                      />
                      <div>
                        <p className="text-sm font-medium text-text-primary leading-none">
                          {r.name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                          {locked && <span className="ml-2 text-xs font-normal text-text-muted">{LOCKED_LABEL}</span>}
                        </p>
                        <p className="text-xs text-text-secondary mt-0.5">{r.description}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="flex justify-between pt-2 border-t border-border-subtle">
              <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={inviteMutation.isPending || !form.email || form.selectedRoleIds.length === 0}>
                <UserPlus className="h-4 w-4 mr-1.5" /> Send Invitation
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Edit profile modal ────────────────────────────────────────────────────────

function EditMemberModal({ member, onClose }: { member: TeamMember; onClose: () => void }) {
  const updateMutation = useUpdateTeamMember();
  const [form, setForm] = useState({
    display_name: member.display_name ?? '',
    first_name: member.first_name ?? '',
    last_name: member.last_name ?? '',
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    updateMutation.mutate(
      { id: member.id, ...form },
      {
        onSuccess: () => { toast.success('Profile updated'); onClose(); },
        onError: () => toast.error('Failed to update profile'),
      },
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Edit Profile — {member.email}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Display Name</Label>
            <Input value={form.display_name} onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>First Name</Label>
              <Input value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Last Name</Label>
              <Input value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} />
            </div>
          </div>
          <p className="text-xs text-text-muted">The email address identifies the account and cannot be changed.</p>
          <div className="flex justify-between pt-2 border-t border-border-subtle">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={updateMutation.isPending}>Save Changes</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Change role modal ─────────────────────────────────────────────────────────

function ChangeRoleModal({ member, options, onClose }: { member: TeamMember; options: RoleOptions; onClose: () => void }) {
  const updateRolesMutation = useUpdateMemberRoles();
  const currentRoleIds = member.roles
    .map((name) => options.nameToId[name])
    .filter((id): id is string => !!id);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Manage Roles — {member.email}</DialogTitle></DialogHeader>
        <RoleAssignmentPanel
          userId={member.id}
          assignableRoles={options.roles}
          currentRoleIds={currentRoleIds}
          lockedRoleIds={options.lockedIds}
          lockedLabel={LOCKED_LABEL}
          hardBlockCombos={[]}
          warnCombos={[]}
          mode="tenant"
          onSave={(diff) => updateRolesMutation.mutateAsync({ id: member.id, ...diff })}
          onSaveSuccess={() => { toast.success('Roles updated'); onClose(); }}
        />
      </DialogContent>
    </Dialog>
  );
}

// ── Reset MFA confirm dialog ──────────────────────────────────────────────────

function ResetMfaDialog({ member, onClose }: { member: TeamMember; onClose: () => void }) {
  const resetMfaMutation = useResetTeamMemberMfa();
  const qc = useQueryClient();
  const { tenantId } = useAuthStore();

  function handleConfirm() {
    resetMfaMutation.mutate(member.id, {
      onSuccess: () => {
        toast.success('MFA reset — user must re-enrol on next login');
        qc.invalidateQueries({ queryKey: ['team-members', tenantId] });
        onClose();
      },
      onError: (err: any) => {
        const code = err?.response?.data?.errors?.[0]?.code;
        if (code === 'MFA_NOT_ENROLLED') {
          toast.error('This user has not enrolled MFA');
        } else {
          toast.error('Failed to reset MFA');
        }
        onClose();
      },
    });
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldOff className="h-4 w-4 text-status-error" />
            Reset MFA for {member.email}?
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-text-secondary">
          This user will be signed out of all active sessions within 15 minutes.
          They will be required to set up MFA again at their next login.
          They will receive an email notification.
        </p>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            variant="destructive"
            size="sm"
            disabled={resetMfaMutation.isPending}
            onClick={handleConfirm}
          >
            {resetMfaMutation.isPending ? 'Resetting…' : 'Reset MFA'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Member action menu ────────────────────────────────────────────────────────

// What this actor may do to this member — the server's matrix, mirrored so the menu offers only what it would accept:
//   a super_admin or user_admin target is managed by a super_admin only; resets reach any target but a super_admin;
//   a role change or removal is never offered on the actor's own row. Nor are the resets — a signed-in user changes
//   their own password and MFA in Profile (the server still accepts a self-reset; this is the menu only).
export function memberActionsFor(member: TeamMember, isSelf: boolean, actor: Pick<UserAdminActor, 'isSuperAdmin'>) {
  const holdsSuperAdmin = member.roles.includes('super_admin');
  const elevated = holdsSuperAdmin || member.roles.includes('user_admin');
  const canManage = actor.isSuperAdmin || !elevated;
  const canReset = (actor.isSuperAdmin || !holdsSuperAdmin) && !isSelf;
  const pending = member.status === 'pending' || member.status === 'lapsed';
  const sso = !!member.auth_method?.startsWith('sso');
  return {
    edit: canManage,
    changeRole: canManage && !isSelf,
    resend: canManage && pending,
    resetPassword: canReset && !pending && !sso,
    resetMfa: canReset,
    remove: canManage && !isSelf,
  };
}

function MemberActions({ member, isSelf, actor, options }: { member: TeamMember; isSelf: boolean; actor: UserAdminActor; options: RoleOptions }) {
  const [editOpen, setEditOpen] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const [resetMfaOpen, setResetMfaOpen] = useState(false);
  const resetPasswordMutation = useResetTeamMemberPassword();
  const resendMutation = useResendTeamMemberInvitation();
  const deleteMutation = useDeleteTeamMember();
  const can = memberActionsFor(member, isSelf, actor);

  function handleResetPassword() {
    resetPasswordMutation.mutate(member.id, {
      onSuccess: () => toast.success('Password-reset link emailed'),
      onError: () => toast.error('Failed to send reset email'),
    });
  }

  function handleResend() {
    resendMutation.mutate(member.id, {
      onSuccess: () => toast.success('Invitation resent'),
      onError: () => toast.error('Failed to resend invitation'),
    });
  }

  function handleDelete() {
    if (!window.confirm(`Delete ${member.email}? This cannot be undone.`)) return;
    deleteMutation.mutate(member.id, {
      onSuccess: () => toast.success('User deleted'),
      onError: (err: any) => toast.error(err?.response?.data?.errors?.[0]?.message ?? 'Failed to delete user'),
    });
  }

  const mfaNotEnrolled = !member.mfa_enrolled_at;
  if (!Object.values(can).some(Boolean)) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" aria-label={`Actions for ${member.email}`}>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          {can.edit && <DropdownMenuItem onClick={() => setEditOpen(true)}>Edit Profile</DropdownMenuItem>}
          {can.changeRole && <DropdownMenuItem onClick={() => setRoleOpen(true)}>Change Role</DropdownMenuItem>}
          {(can.resend || can.resetPassword || can.resetMfa) && <DropdownMenuSeparator />}
          {can.resend && (
            <DropdownMenuItem onClick={handleResend} disabled={resendMutation.isPending}>
              Resend Invitation
            </DropdownMenuItem>
          )}
          {can.resetPassword && (
            <DropdownMenuItem onClick={handleResetPassword} disabled={resetPasswordMutation.isPending}>
              Email password-reset link
            </DropdownMenuItem>
          )}
          {can.resetMfa && (
            <DropdownMenuItem
              onClick={() => !mfaNotEnrolled && setResetMfaOpen(true)}
              disabled={mfaNotEnrolled}
              title={mfaNotEnrolled ? 'User has not yet enrolled MFA' : undefined}
            >
              Reset MFA (re-enrol at next login)
            </DropdownMenuItem>
          )}
          {can.remove && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="text-accent-rose focus:text-accent-rose"
              >
                Delete User
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {editOpen && <EditMemberModal member={member} onClose={() => setEditOpen(false)} />}
      {roleOpen && <ChangeRoleModal member={member} options={options} onClose={() => setRoleOpen(false)} />}
      {resetMfaOpen && <ResetMfaDialog member={member} onClose={() => setResetMfaOpen(false)} />}
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

// Every signed-in tenant user sees the list (READ-ONLY on user management — business roles keep their access
// everywhere else); managing users is for a super_admin or user_admin, as the server reads it.
export function TeamPage() {
  const { data: members, isLoading } = useTeamMembers();
  const { data: actor } = useUserAdminActor();
  const { user } = useAuthStore();
  const [inviteOpen, setInviteOpen] = useState(false);

  useEffect(() => { document.title = 'Users & Permissions — VeriGRC'; }, []);
  const canAdminister = actor?.canAdministerUsers === true;
  const options = roleOptions(actor);

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Users & Permissions</h1>
          <p className="mt-1 text-sm text-text-secondary">
            {canAdminister
              ? 'Manage team members and their roles'
              : 'Your team members and their roles. You have read-only access to user management.'}
          </p>
        </div>
        {canAdminister && (
          <Button size="sm" onClick={() => setInviteOpen(true)}>
            <UserPlus className="h-4 w-4 mr-1.5" /> Invite Member
          </Button>
        )}
      </div>

      <PrimaryAdminReferenceCard />

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Team Members</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border-subtle bg-bg-surface">
                    <th className="px-4 py-3 text-left font-medium text-text-secondary">Name</th>
                    <th className="px-4 py-3 text-left font-medium text-text-secondary">Email</th>
                    <th className="px-4 py-3 text-left font-medium text-text-secondary">Auth</th>
                    <th className="px-4 py-3 text-left font-medium text-text-secondary">Roles</th>
                    <th className="px-4 py-3 text-left font-medium text-text-secondary">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-text-secondary">Last Login</th>
                    {canAdminister && <th className="px-4 py-3" />}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-subtle">
                  {(members ?? []).map((m) => (
                    <tr key={m.id} className="bg-bg-surface hover:bg-bg-surface-2">
                      <td className="px-4 py-3">
                        <p className="font-medium text-text-primary">
                          {m.display_name || [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-xs text-text-secondary">{m.email}</td>
                      <td className="px-4 py-3">
                        {m.auth_method?.startsWith('sso')
                          ? <SsoUserBadge authMethod={m.auth_method} size="sm" />
                          : <span className="text-xs text-text-muted">Local</span>
                        }
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {m.roles.map((r) => (
                            <Badge key={r} variant="muted" className="text-[10px]">{r.replace(/_/g, ' ')}</Badge>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={m.status === 'active' ? 'emerald' : 'muted'} className="text-[10px]">{m.status}</Badge>
                      </td>
                      <td className="px-4 py-3 text-xs text-text-secondary">
                        {m.last_login_at
                          ? formatDistanceToNow(parseISO(m.last_login_at), { addSuffix: true })
                          : 'Never'}
                      </td>
                      {canAdminister && actor && (
                        <td className="px-4 py-3 text-right">
                          <MemberActions member={m} isSelf={m.id === user?.id} actor={actor} options={options} />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {inviteOpen && <InviteModal options={options} onClose={() => setInviteOpen(false)} />}
    </div>
  );
}
